<template>
  <section>
    <div>
      <p class="text-left">Please review and confirm the beneficial owners.</p>
      <div
        class="mt-6 flex gap-3"
        :class="layoutClass"
      >
        <div
          v-if="owners.length > 0"
          class="flex w-full flex-col gap-3"
        >
          <div
            v-for="(owner, index) in owners"
            :key="index"
            class="flex w-full items-center gap-2 rounded-lg bg-off-white px-3 py-5"
          >
            <div class="justify-left flex w-full flex-row items-center gap-2">
              <IaaoIcon />
              <div class="text-left">
                <p
                  v-if="displayUserName(owner)"
                  class="text-xs text-neutral-subtle md:text-sm"
                >
                  {{ owner?.first_name }} {{ owner?.last_name }}
                </p>
                <p
                  :class="[
                    'text-xs md:text-sm',
                    displayUserName(owner)
                      ? 'text-neutral-subtlest'
                      : 'text-neutral-subtle',
                  ]"
                >
                  {{ formatOwnerEmail(owner) }}
                </p>
                <p
                  v-if="owner.job_title?.length > 0"
                  class="text-xs font-normal text-neutral-subtle md:text-sm"
                >
                  {{ `${owner.job_title}${owner?.is_type ? ',' : ''}` }}
                  <span v-if="owner?.job_title"
                    >({{ formatOwnerType(owner.is_type) }})
                  </span>
                </p>
              </div>
            </div>
            <div v-if="owner.ownership_percentage">
              <div class="flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M10.5 6C9.01664 6 7.56659 6.43987 6.33322 7.26398C5.09985 8.08809 4.13856 9.25943 3.5709 10.6299C3.00325 12.0003 2.85472 13.5083 3.14411 14.9632C3.4335 16.418 4.1478 17.7544 5.1967 18.8033C6.24559 19.8522 7.58196 20.5665 9.03682 20.8559C10.4917 21.1453 11.9997 20.9968 13.3701 20.4291C14.7406 19.8614 15.9119 18.9001 16.736 17.6668C17.5601 16.4334 18 14.9834 18 13.5H10.5V6Z"
                    fill="#374053"
                    fill-opacity="0.1"
                    stroke="#6C7C9D"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.5 10.5H21C21 8.51088 20.2098 6.60322 18.8033 5.1967C17.3968 3.79018 15.4891 3 13.5 3V10.5Z"
                    fill="#374053"
                    fill-opacity="0.1"
                    stroke="#6C7C9D"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <p
                  class="text-xs font-normal text-neutral-subtle-bold md:text-sm"
                >
                  {{
                    parseFloat(owner.ownership_percentage) % 1 === 0
                      ? parseInt(owner.ownership_percentage)
                      : owner.ownership_percentage
                  }}%
                </p>
              </div>
            </div>
          </div>
          <radio-button
            v-if="userIsControllingManager"
            :item="'I have provided all beneficial owners (people with 25% or more ownership) of the business.'"
            :active="checkboxSelected"
            :handle-select="onCheckboxSelect"
            :disabled="isAttested"
            name="checkbox-confirm"
            style="padding-top: 0.5rem; padding-bottom: 0.5rem"
          />
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center gap-3 rounded-md bg-off-white py-8"
        >
          <p class="text-gray-over-dark">
            You haven’t added any beneficial owners yet
          </p>
        </div>
      </div>
      <form-navigation-buttons
        v-if="!isNavigationDisabled"
        :enabled="!isNavigationDisabled"
        :component-identifier="componentIdentifier"
        :buttons-to-display="buttonsToDisplay"
        @next="handleNext"
        @back="handleBack"
        @edit="handleEdit"
      />
      <add-owner-modal
        :visible="openModal"
        @close="closeModal"
      />
    </div>
  </section>
</template>

<script setup>
import { useStore } from 'vuex';
import { ref, computed, watch } from 'vue';
import { useFormRouter } from '@/composables/formRouter';
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import AddOwnerModal from '@/components/modals/AddOwnerModal.vue';
import IaaoIcon from '@/components/icons/IaaoIcon.vue';
import RadioButton from '@/components/RadioButton.vue';

const componentIdentifier = 'Ownership-2';
const buttonsToDisplay = 'continue-save';
const store = useStore();
const formRouter = useFormRouter(componentIdentifier);

// const isSubmitButtonDisabled = computed(() => store.state.submitButtonDisabled);

const firstTime = computed(
  () =>
    store.state.firstTime?.[formRouter.componentStep.value?.firstTimeIdentifier]
);
const isNavigationDisabled = computed(() => formRouter.isDisabled.value);

const owners = computed(
  () => store.state.ownership.ownershipData?.ownership_users || []
);

const userIsControllingManager = computed(
  () =>
    store.state.ownership.ownershipData?.ownership_users?.find(
      (owner) => owner.user_id === store.state.users.currentUser.id
    )?.is_type == 'controlling_manager' || null
);

const isAttested = computed(() =>
  owners?.value?.find((ow) => ow?.attested_all_beneficial_owners_at?.length)
);

const userOwnership = computed(
  () =>
    store.state.ownership.ownershipData?.ownership_users?.find(
      (owner) => owner.user_id === store.state.users.currentUser.id
    ) || null
);

const handleNext = () => {
  if (userIsControllingManager.value && checkboxSelected.value) {
    store.dispatch('ownership/recordAttestation', userOwnership.value.id);
  }
  formRouter.moveToNextStep();
};
const handleBack = () => {
  formRouter.moveToPreviousStep();
  console.log('Back');
};
const handleEdit = () => {
  console.log('Edit');
};
const isActive = ref('');
const openModal = ref(false);
const checkboxSelected = ref(false);

const closeModal = () => {
  openModal.value = false;
  isActive.value = '';
};

const onCheckboxSelect = () => {
  if (isAttested.value) return;

  checkboxSelected.value = !checkboxSelected.value;
  store.commit('toggleDisabledButton', {
    bool: !checkboxSelected.value,
    source: 'OwnershipStepTwo #1',
  });
};
const layoutClass = computed(() => 'flex-col');

function formatOwnerEmail(owner) {
  return owner.user_id === store.state.users.currentUser.id
    ? store.state.users.currentUser.email
    : (owner.invited_user_email ?? '-');
}

function formatOwnerType(type) {
  return type === 'controlling_manager' ? 'Controlling Manager' : 'Owner';
}

const displayUserName = (owner) => {
  return owner.first_name?.length > 0 && owner.last_name?.length > 0;
};
watch(
  () => store.state.formRoutes?.activeStep?.identifier,
  async (newVal) => {
    if (newVal !== componentIdentifier) return;

    const { currentUser } = store.state.users;
    const isControllingManager = userIsControllingManager.value;

    await store.dispatch(
      'ownership/getOwnershipByCompanyId',
      currentUser.company.id
    );

    if (isAttested.value) {
      checkboxSelected.value = true;
      store.commit('toggleDisabledButton', {
        bool: false,
        source: 'OwnershipStepTwo #3',
      });
    }

    store.commit('toggleDisabledButton', {
      bool: isControllingManager ? !checkboxSelected.value : false,
      source: `OwnershipStepTwo #${isControllingManager ? (checkboxSelected.value ? 1 : 2) : 3}`,
    });

    if (!isControllingManager && owners.value.length > 0 && firstTime.value) {
      store.commit('firstTime/updateStepFirstTime', {
        step: 'Ownership',
        value: false,
      });
    }
  }
);
</script>
